import IErrorReport, { instanceOfIErrorReport } from "@/types/IErrorReport";
import ILogin, { instanceOfILogin } from "@/types/ILogin";
import ILoginResponse, { instanceOfILoginResponse } from "@/types/ILoginResponse";
import IPedido, { instanceOfIPedido } from "@/types/IPedido";
import IPedidoLead from "@/types/IPedidoLead";
import IRecover, { instanceOfIRecover } from "@/types/IRecover";
import IRegister, { instanceOfIRegister } from "@/types/IRegister";
import IRegisterResponse, { instanceOfIRegisterResponse } from "@/types/IRegisterResponse";
import ITelefone, { instanceOfITelefone } from "@/types/ITelefone";
import IVoucher, { instanceOfIVoucher } from "@/types/IVoucher";
import PedidoResponse, { instanceOfPedidoResponse } from "@/types/PedidoResponse";
import Nullable from "@/types/utils/Nullable";
import RequestError, { instanceOfAxiosError } from "@/types/utils/RequestError";
import Apm from "@/utils/Apm";
import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { createApiClient } from "@/utils/httpClient";
import loadDataset from "@/utils/load-dataset";

const api = createApiClient({
    baseURL: process.env.NEXT_PUBLIC_API_URL || 'https://assine3-be.estadao.com.br/',
    headers: {
        Authorization: process.env.API_TOKEN || ''
    },
})

export const setTelefone = async (telefone: ITelefone): Promise<void> => {
    if (!instanceOfITelefone(telefone)) {
        Apm.captureError(`ERRO NO UPDATE TELEFONE, OBJETO ENVIADO: ${JSON.stringify(telefone)}`)
        throw new Error(`ERRO NO UPDATE TELEFONE, OBJETO ENVIADO: ${JSON.stringify(telefone)}`)
    }

    api.post('v1/telefone', telefone, { withCredentials: true })
        .catch((err: RequestError) => {
            throw new Error(err.message)
        })
}

const isInvalidOfferError = (obj: any): boolean => {
    if (!obj) return false;
    const text =
        (obj?.response?.data?.message as string) ||
        (obj?.message as string) ||
        (typeof obj === 'string' ? obj : '');
    return /oferta/i.test(text) && /inv[aá]lida|invalid/i.test(text);
}

export const createPedido = async (pedido: IPedido, withCredentials: boolean = false): Promise<PedidoResponse> => {
    return new Promise((resolve, reject) => {
        let config: AxiosRequestConfig = {
            withCredentials
        }
        const genericError = "Falha na comunicação, tente novamente!"

        if (!instanceOfIPedido(pedido)) {
            Apm.captureError(`ERRO NO CREATE PEDIDO, OBJETO ENVIADO: ${JSON.stringify(pedido)}`)
            reject(genericError)
        }

        api.post('v1/submit', pedido, config)
            .then((response: AxiosResponse<PedidoResponse>) => {
                if (!instanceOfPedidoResponse(response.data)) {
                    reject(genericError)
                }
                resolve(response.data)
            }).catch((err: RequestError) => {
                if (instanceOfAxiosError(err)) {
                    reject(err)
                }
                reject(err)
            })
    })
}

export const createPedidoLead = async (pedido: IPedidoLead, withCancellation: boolean = false): Promise<PedidoResponse> => {
    let config: AxiosRequestConfig = {}

    if (withCancellation) {
        let source = axios.CancelToken.source();
        config.cancelToken = source.token
    }

    if (!pedido.name || !pedido.email) {
        throw new Error(`Faltou o email ou name na requisição name: ${pedido.name} email: ${pedido.email}`)
    }

    return api.post('v1/submit/lead', {
        ...pedido,
        dataset: loadDataset()
    }, config)
        .then((response: AxiosResponse<PedidoResponse>) => {
            if (!instanceOfPedidoResponse(response.data)) {
                Apm.captureError(`ERRO NO SUBMIT LEAD, OBJETO ENVIADO: ${JSON.stringify(pedido)}, OBJETO RECEBIDO: ${JSON.stringify(response.data)}`);
            }
            return response.data
        }).catch(() => {
            throw new Error('')
        })
}

export const validateEmailRegister = async (email: string): Promise<boolean | void> => {
    try {
        await axios.post("https://api.estadao.com.br/users/register", { email_login: email })
    } catch (error: any) {
        if (error.response?.status === 409) {
            return true
        } else if (error.response?.status === 400) {
            return false
        }
        throw error
    }
}

export const login = async (login: ILogin): Promise<ILoginResponse> => {
    return new Promise((resolve, reject) => {
        const genericError = "Não foi possível realizar o login"
        if (!instanceOfILogin(login)) {
            Apm.captureError(`ERRO NO LOGIN, OBJETO ENVIADO: ${JSON.stringify(login)}`)
            reject(genericError)
        }

        api.post('v1/auth/login', login)
            .then((response: AxiosResponse<ILoginResponse>) => {
                if (!instanceOfILoginResponse(response.data)) {
                    Apm.captureError(`ERRO NO LOGIN, OBJETO ENVIADO: ${JSON.stringify(login)}, OBJETO RECEBIDO: ${JSON.stringify(response.data)}`);
                    reject(genericError)
                }
                resolve(response.data)
            }).catch((err: RequestError) => {
                if ((err as any).response?.data?.error?.code === 400 && (err as any).response?.data?.error?.message) {
                    reject((err as any).response.data.error.message)
                }
                reject(err.message)
            })
    })
}

export const createUser = async (user: IRegister): Promise<IRegisterResponse> => {
    return new Promise((resolve, reject) => {
        if (!instanceOfIRegister(user)) {
            Apm.captureError(`ERRO NO CREATE USER, OBJETO ENVIADO: ${JSON.stringify(user)}`)
            reject('Não foi possível processar a sua solicitação!')
        }

        api.post('v1/auth/register', user, { withCredentials: true })
            .then((response: AxiosResponse<IRegisterResponse>) => {
                if (!instanceOfIRegisterResponse(response.data)) {
                    Apm.captureError(`ERRO NO REGISTER, OBJETO ENVIADO: ${JSON.stringify(user)}, OBJETO RECEBIDO: ${JSON.stringify(response.data)}`);
                }
                resolve(response.data);
            })
            .catch((err: RequestError) => {
                reject(err)
            })
    })
}

export const recoverPassword = async (recoverData: IRecover): Promise<void> => {
    if (!instanceOfIRecover(recoverData)) {
        Apm.captureError(`ERRO NO RECOVER PASSWORD, OBJETO ENVIADO: ${JSON.stringify(recoverData)}`)
    }

    api.post('v1/auth/recovery', recoverData, { withCredentials: true })
        .catch(() => {
            throw new Error("")
        })
}

export const getVoucher = async (offerID: string, parceiroID: Nullable<string> = null): Promise<IVoucher> => {
    let offer: string = `${offerID}`

    if (parceiroID) {
        offer = `${parceiroID}/${offer}`
    }

    return api.get(`v1/search/voucher/${offer}`)
        .then((response: AxiosResponse<IVoucher>) => {
            if (!instanceOfIVoucher(response.data)) {
                Apm.captureError(`ERRO NO GET VOUCHER, OBJETO RECEBIDO: ${JSON.stringify(response.data)}`);
            }
            return response.data
        })
        .catch(() => {
            throw new Error("")
        })
}

export const reportFrontEndError = async (errorReport: IErrorReport): Promise<void> => {
    if (!instanceOfIErrorReport(errorReport)) {
        Apm.captureError(`ERRO NO REPORT FRONT END ERROR, OBJETO ENVIADO: ${JSON.stringify(errorReport)}`)
    }

    api.post('/v1/front-error-report', errorReport, { withCredentials: true })
        .catch(() => {
            // já capturado pelo interceptor central de erros do httpClient
        })
}
